import './FormItem.scss';

import {Form, Input, TimePicker} from 'antd';
import cn from 'classnames';
import React, {useState} from 'react';

import * as formItemTypes from '../../consts/form/formItemTypes';
import useDidUpdate from '../../hooks/useDidUpdate';
import AntDatePicker from '../AntDatePicker/AntDatePicker';
import ImageUpload from '../ImageUpload/ImageUpload';
import FormApiKeyGenerator from './components/ApiKeyGenerator';
import FormCheckbox from './components/FormCheckbox';
import FormHelper from './components/FormHelper';
import FormRadioButton from './components/FormRadioButton';
import FormSelect from './components/FormSelect';
import PasswordInput from './components/PasswordInput';
import PollingScheduleEditor from './components/PollingScheduleEditor';

const FormItem = (props) => {
    const {
        name,
        label,
        prefix,
        suffix,
        pattern,
        form,
        required = false,
        className,
        itemClasses,
        initialValue,
        fieldLabel,
        handleRadioChange,
        handleDatePick,
        handleTimePick,
        handleSelect,
        handleChange,
        defaultValue,
        format,
        compareWith,
        validator,
        checkboxOptions,
        errorMessage,
        feedbackContent,
        radioButtonOptions,
        formItemType = formItemTypes.INPUT,
        disabled,
        requireDeps,
        itemDeps,
        preserve = false,
        inlineFeedback = false,
        ...restProps
    } = props;

    const [fieldState, setFieldState] = useState({isValid: true, value: initialValue});
    const currentValue = Form.useWatch(name, form);
    const requireValue = Form.useWatch(requireDeps, form);
    const itemDepsValue = Form.useWatch(itemDeps, form);

    const isFunction = (value) => typeof value === 'function';
    const isItemRequired = isFunction(required) ? required(requireValue, itemDepsValue) : required;
    const isDisabled = isFunction(disabled) ? disabled(requireValue, itemDepsValue) : disabled;
    const isFeedbackVisible = (isItemRequired && !fieldState.isValid) || (fieldState.value && !fieldState.isValid);

    useDidUpdate(() => {
        if (formItemType === formItemTypes.SELECT && isItemRequired && currentValue) {
            setFieldState({isValid: !!currentValue, value: currentValue});
        }
    }, [currentValue]);

    const validateField = (_, value) => {
        if (validator && isItemRequired) {
            const isValid = validator(value);

            setFieldState({isValid, value});
            return isValid ? Promise.resolve() : Promise.reject();
        }
        if (compareWith && value) {
            const isValid = form.getFieldValue(compareWith) === value;

            setFieldState({isValid, value});
            return isValid ? Promise.resolve() : Promise.reject();
        }
        if (pattern && value) {
            const isValid = value.match(pattern);

            setFieldState({isValid, value});
            return isValid ? Promise.resolve() : Promise.reject();
        }
        setFieldState({isValid: !!value, value});
        if (!isItemRequired) return Promise.resolve();
        return value ? Promise.resolve() : Promise.reject();
    };

    const options = {
        className: cn('text-start m-0', itemClasses),
        initialValue,
        preserve,
        name,
        help: (
            <FormHelper
                inline={inlineFeedback}
                errorMessage={errorMessage}
                content={fieldState.value && feedbackContent}
                isVisible={isFeedbackVisible}
            />
        ),
        rules: [{validator: validateField}],
    };

    const inputBaseClasses =
        'h-12 w-full rounded-6 text-16 transition-shadow duration-200 hover:shadow-grid_cell focus-within:shadow-grid_cell disabled:bg-wild-sand disabled:text-manatee disabled:cursor-not-allowed disabled:hover:shadow-none disabled:border-divider';

    const contentMapper = {
        [formItemTypes.CHECKBOX]: (
            <FormCheckbox name={name} form={form} options={checkboxOptions} disabled={isDisabled} {...props} />
        ),
        [formItemTypes.DATE_PICKER]: (
            <AntDatePicker
                disabled={isDisabled}
                onChange={handleDatePick}
                form={form}
                name={name}
                placeholder={restProps?.placeholder}
                suffix={suffix}
                {...restProps}
                className={cn(inputBaseClasses, restProps.className)}
            />
        ),
        [formItemTypes.TIME_PICKER]: (
            <TimePicker
                format={format}
                disabled={isDisabled}
                onChange={handleTimePick}
                form={form}
                name={name}
                prefixCls='ep-form-item-timepicker'
                className={inputBaseClasses}
                placeholder={restProps?.placeholder}
                suffix={suffix}
                defaultValue={defaultValue}
                {...restProps}
            />
        ),
        [formItemTypes.SELECT]: (
            <FormSelect
                fieldLabel={fieldLabel}
                form={form}
                name={name}
                disabled={isDisabled}
                onSelect={handleSelect}
                onChange={handleChange}
                suffix={suffix}
                {...restProps}
            />
        ),
        [formItemTypes.MULTIPLE_SELECT]: (
            <FormSelect
                mode='multiple'
                fieldLabel={fieldLabel}
                form={form}
                name={name}
                disabled={isDisabled}
                onChange={handleChange}
                suffix={suffix}
                {...restProps}
            />
        ),
        [formItemTypes.PASSWORD]: (
            <PasswordInput
                disabled={isDisabled}
                name={Array.isArray(name) ? name.join('_') : name}
                aria-required={isItemRequired}
                required={isItemRequired}
                {...restProps}
                className={inputBaseClasses}
            />
        ),
        [formItemTypes.RADIO]: (
            <FormRadioButton
                initialValue={currentValue}
                disabled={isDisabled}
                name={name}
                onChange={(e) => handleRadioChange(e.target.value, name)}
                options={radioButtonOptions}
                form={form}
                suffix={suffix}
                {...restProps}
            />
        ),
        [formItemTypes.INPUT]: (
            <Input
                name={Array.isArray(name) ? name.join('_') : name}
                title={currentValue}
                size='large'
                disabled={isDisabled}
                prefix={!!prefix && <span>{prefix}</span>}
                suffix={!isDisabled && suffix}
                className={inputBaseClasses}
                onChange={(e) => handleChange?.(e.target.value, name)}
                {...restProps}
            />
        ),
        [formItemTypes.IMAGE]: <ImageUpload name={name} form={form} />,
        [formItemTypes.API_KEY_GENERATOR]: (
            <FormApiKeyGenerator form={form} name={name} disabled={isDisabled} {...restProps} />
        ),
        [formItemTypes.POLLING_SCHEDULE]: (
            <PollingScheduleEditor disabled={isDisabled} showValidation={isFeedbackVisible} {...restProps} />
        ),
    };

    const content = contentMapper[formItemType] || contentMapper[formItemTypes.INPUT];
    const isPollingSchedule = formItemType === formItemTypes.POLLING_SCHEDULE;
    const {help, ...fieldOptions} = options;

    const isTextInput = formItemType === formItemTypes.INPUT || formItemType === formItemTypes.PASSWORD;
    const Label = isTextInput ? 'span' : 'label';
    const labelComponent = label && (
        <Label
            htmlFor={isTextInput ? undefined : name}
            className={cn(
                'block text-start text-14 font-semibold tracking-01 transition-colors',
                !isTextInput && 'mb-2',
                isDisabled ? 'text-manatee' : 'text-charcoal'
            )}
        >
            {label}
            {isItemRequired && <span className='text-radical-red ml-1'>*</span>}
        </Label>
    );

    return (
        <div className={cn('relative', className)}>
            {!isTextInput && labelComponent}
            {isPollingSchedule ? (
                <>
                    <Form.Item {...fieldOptions} noStyle>
                        {content}
                    </Form.Item>
                    {help}
                </>
            ) : (
                <Form.Item
                    {...options}
                    label={isTextInput ? labelComponent : undefined}
                    labelCol={isTextInput ? {span: 24} : undefined}
                    wrapperCol={isTextInput ? {span: 24} : undefined}
                    labelAlign='left'
                    colon={false}
                    required={false}
                >
                    {content}
                </Form.Item>
            )}
        </div>
    );
};

export default FormItem;
